import React from "react";
import { useParams } from "react-router-dom";
import EndpointFunctions from "../../utils/Functions/endpointFunctions";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useState, useEffect } from "react";
import Endpoints from "../../api/endpoints";
import {
  Card,
  Descriptions,
  Space,
  Tag,
  Spin,
  Alert,
  Input,
  Switch,
  Button,
  message,
  Divider,
  Image,
  Empty,
} from "antd";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ClientStatistics from "./components/ClientStatistics";
import { FaStamp, FaGift, FaCalendarAlt, FaClock } from "react-icons/fa";
import UtilFunctions from "../../utils/Functions/utilFunctions";

const ClientDetails = () => {
  const params = useParams();
  const clientId = params.clientId;
  const axiosPrivate = useAxiosPrivate();
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editedClient, setEditedClient] = useState(null);
  const [isEdited, setIsEdited] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [isUpdatingCard, setIsUpdatingCard] = useState(false);

  const fetchClient = async () => {
    try {
      setIsLoading(true);
      const response = await EndpointFunctions.fetchData({
        endpointURL: Endpoints.getClient,
        axiosPrivate: axiosPrivate,
        httpParams: {
          clientId: clientId,
        },
      });
      setClient(response.data);
      setEditedClient(response.data);

      // Set phone values if phoneNumber exists
      if (response.data.clientDetails.phoneNumber) {
        // For Turkish numbers, we know the country code is 90
        setCountryCode("90");
        setPhoneNumber(response.data.clientDetails.phoneNumber);
      }
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
  }, [clientId, axiosPrivate]);

  const handleInputChange = (field, value) => {
    setEditedClient((prev) => ({
      ...prev,
      clientDetails: {
        ...prev.clientDetails,
        [field]: value,
      },
    }));
    setIsEdited(true);
  };

  const handlePhoneChange = (value, country) => {
    if (typeof value === "string") {
      const phoneNumberWithoutCountryCode = value.substring(
        country.dialCode.length
      );
      setCountryCode(country.dialCode);
      setPhoneNumber(phoneNumberWithoutCountryCode);
      setIsEdited(true);
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    const requestBody = {
      clientId: editedClient.clientDetails.clientId,
      name: editedClient.clientDetails.name,
      email: editedClient.clientDetails.email,
      countryCode: parseInt(countryCode),
      phoneNumber: phoneNumber,
    };

    const result = await EndpointFunctions.putData({
      endpointURL: Endpoints.updateClientDetails,
      axiosPrivate: axiosPrivate,
      data: requestBody,
    });

    if (result.success) {
      message.success("Değişiklikler başarıyla kaydedildi");
      setClient(editedClient);
      setIsEdited(false);
    } else {
      message.error("Değişiklikler kaydedilemedi");
    }

    setIsSubmitting(false);
  };

  const handleCardUpdate = async (clientCardId, newStampCount) => {
    setIsUpdatingCard(true);
    try {
      const result = await EndpointFunctions.putData({
        endpointURL: Endpoints.updateClientCard,
        axiosPrivate: axiosPrivate,
        data: {
          clientCardId: clientCardId,
          stampCount: newStampCount,
        },
      });

      if (result.success) {
        message.success("Kart bilgileri güncellendi");
        await fetchClient(); // Refetch all client data after successful update
      } else {
        message.error("Kart bilgileri güncellenemedi");
      }
    } catch (error) {
      message.error("Bir hata oluştu");
    } finally {
      setIsUpdatingCard(false);
    }
  };

  if (error) {
    return (
      <Alert
        message="Hata"
        description="Müşteri bilgileri yüklenemedi"
        type="error"
        showIcon
      />
    );
  }

  return (
    <Spin spinning={isUpdatingCard || isLoading}>
      <Space
        direction="vertical"
        size="large"
        style={{ width: "100%", padding: "24px" }}>
        <Card
          type="inner"
          title="Müşteri Bilgileri"
          extra={
            isEdited && (
              <Button type="primary" onClick={handleSave}>
                Kaydet
              </Button>
            )
          }>
          <Descriptions column={2}>
            <Descriptions.Item label="Müşteri ID">
              {editedClient?.clientDetails?.clientId}
            </Descriptions.Item>
            <Descriptions.Item label="Kullanıcı ID">
              {editedClient?.clientDetails?.userId}
            </Descriptions.Item>
            <Descriptions.Item label="Ad Soyad">
              <Input
                value={editedClient?.clientDetails?.name || ""}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Ad soyad giriniz"
              />
            </Descriptions.Item>
            <Descriptions.Item label="E-posta">
              <Input
                value={editedClient?.clientDetails?.email || ""}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="E-posta giriniz"
              />
            </Descriptions.Item>
            <Descriptions.Item label="Telefon Numarası" span={2}>
              <PhoneInput
                value={
                  countryCode && phoneNumber
                    ? `${countryCode}${phoneNumber}`
                    : ""
                }
                country="tr"
                countryCodeEditable={true}
                onChange={handlePhoneChange}
                inputProps={{
                  required: true,
                }}
                containerStyle={{
                  width: "35%",
                }}
                inputStyle={{
                  width: "100%",
                  height: "32px",
                  fontSize: "14px",
                  borderRadius: "6px",
                  border: "1px solid #d9d9d9",
                }}
                buttonStyle={{
                  border: "1px solid #d9d9d9",
                  borderRight: "none",
                  borderRadius: "6px 0 0 6px",
                  backgroundColor: "#fff",
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Telefon Numarası Durumu">
              <Tag
                color={
                  editedClient?.clientDetails?.isPhoneNumberAdded
                    ? "green"
                    : "red"
                }>
                {editedClient?.clientDetails?.isPhoneNumberAdded
                  ? "Ekli"
                  : "Ekli Değil"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Kayıt Kanalı">
              {editedClient?.clientDetails?.registrationChannel === "WEB" ? (
                <Tag color="geekblue">Web</Tag>
              ) : editedClient?.clientDetails?.registrationChannel === "APP" ? (
                <Tag color="green">Uygulama</Tag>
              ) : (
                <Tag>Bilinmiyor</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Apple Wallet">
              {editedClient?.clientDetails?.hasAppleWalletPass ? (
                <Tag color="green">Aktif kart var</Tag>
              ) : (
                <Tag>Yok</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Bildirim İzinleri" type="inner">
          <Descriptions column={3}>
            <Descriptions.Item label="E-posta Bildirimleri">
              <Switch
                checked={
                  editedClient?.clientDetails?.notificationPreferences?.GENERAL
                    ?.permitEmail
                }
                disabled
              />
            </Descriptions.Item>
            <Descriptions.Item label="SMS Bildirimleri">
              <Switch
                checked={
                  editedClient?.clientDetails?.notificationPreferences?.GENERAL
                    ?.permitSms
                }
                disabled
              />
            </Descriptions.Item>
            <Descriptions.Item label="Genel Bildirimler">
              <Switch
                checked={
                  editedClient?.clientDetails?.notificationPreferences?.GENERAL
                    ?.permitNotification
                }
                disabled
              />
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {client?.clientCardDetails && (
          <Card title="Kart Bilgileri" type="inner">
            {client.clientCardDetails.map((card) => (
              <Card
                key={card.id}
                type="inner"
                title={card.shopCardDetails.cardTitle}
                style={{ marginBottom: "16px" }}>
                <Descriptions column={2}>
                  <Descriptions.Item label="Mağaza">
                    {card.shopCardDetails.shopName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ödül">
                    {card.shopCardDetails.prize}
                  </Descriptions.Item>
                  <Descriptions.Item label="Gerekli Damga">
                    {card.shopCardDetails.stampCountForPrize}
                  </Descriptions.Item>
                  <Descriptions.Item label="Mevcut Damga">
                    <Space>
                      <Input
                        type="number"
                        defaultValue={card.stampCount}
                        min={0}
                        max={card.shopCardDetails.stampCountForPrize}
                        style={{ width: "100px" }}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value);
                          if (isNaN(newValue)) return;
                          setEditedClient((prev) => ({
                            ...prev,
                            clientCardDetails: prev.clientCardDetails.map((c) =>
                              c.id === card.id
                                ? { ...c, stampCount: newValue }
                                : c
                            ),
                          }));
                        }}
                      />
                      <span>/ {card.shopCardDetails.stampCountForPrize}</span>
                      <Button
                        type="primary"
                        size="small"
                        loading={isUpdatingCard}
                        onClick={() => {
                          const editedCard =
                            editedClient.clientCardDetails.find(
                              (c) => c.id === card.id
                            );
                          if (
                            editedCard &&
                            editedCard.stampCount !== card.stampCount
                          ) {
                            handleCardUpdate(card.id, editedCard.stampCount);
                          }
                        }}>
                        Güncelle
                      </Button>
                    </Space>
                  </Descriptions.Item>
                  {card.shopCardDetails.welcomePrize && (
                    <Descriptions.Item label="Hoşgeldin Hediyesi">
                      {card.shopCardDetails.welcomePrize}
                    </Descriptions.Item>
                  )}
                  {card.shopCardDetails.conditions && (
                    <Descriptions.Item label="Koşullar">
                      {card.shopCardDetails.conditions}
                    </Descriptions.Item>
                  )}
                </Descriptions>
                <Divider />
                {card.statistics && (
                  <div style={{ marginTop: "20px" }}>
                    <Descriptions
                      title={
                        <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                          Kart İstatistikleri
                        </div>
                      }
                      column={{ xs: 1, sm: 2, md: 4 }}
                      size="small"
                      bordered>
                      <Descriptions.Item
                        label={
                          <span>
                            <FaStamp
                              style={{ marginRight: "8px", color: "#1890ff" }}
                            />
                            Damga
                          </span>
                        }>
                        {card.statistics.stampCount}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={
                          <span>
                            <FaGift
                              style={{ marginRight: "8px", color: "#1890ff" }}
                            />
                            Ödül
                          </span>
                        }>
                        {card.statistics.rewardCount}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={
                          <span>
                            <FaCalendarAlt
                              style={{ marginRight: "8px", color: "#1890ff" }}
                            />
                            Kayıt Tarihi
                          </span>
                        }>
                        {new Date(
                          card.statistics.registrationDate
                        ).toLocaleString("tr-TR")}
                      </Descriptions.Item>
                      <Descriptions.Item
                        label={
                          <span>
                            <FaClock
                              style={{ marginRight: "8px", color: "#1890ff" }}
                            />
                            Son Aktivite
                          </span>
                        }>
                        {new Date(
                          card.statistics.lastActivityDate
                        ).toLocaleString("tr-TR")}
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                )}
              </Card>
            ))}
          </Card>
        )}
        <Card title="Diğer Kartlar" type="inner">
          {client?.otherCards && client.otherCards.length > 0 ? (
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {client.otherCards.map((card) => (
                <Card
                  key={card.cardId}
                  type="inner"
                  size="small"
                  style={{ marginBottom: 8 }}>
                  <Space
                    align="center"
                    size="middle"
                    style={{ width: "100%", flexWrap: "wrap" }}>
                    {card.type === "PREDEFINED" && card.logoUrl ? (
                      <Image
                        src={card.logoUrl}
                        alt={card.companyName}
                        width={48}
                        height={48}
                        style={{ objectFit: "contain" }}
                        preview={false}
                      />
                    ) : (
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          backgroundColor:
                            UtilFunctions.convertDecimalToColor(card.color),
                          border: "1px solid #ddd",
                        }}
                      />
                    )}
                    <Descriptions
                      column={{ xs: 1, sm: 2, md: 3 }}
                      size="small"
                      style={{ flex: 1, minWidth: 320 }}>
                      <Descriptions.Item label="Marka">
                        <Space>
                          {card.companyName || "-"}
                          <Tag
                            color={
                              card.type === "PREDEFINED" ? "blue" : "purple"
                            }>
                            {card.type === "PREDEFINED" ? "Hazır" : "Özel"}
                          </Tag>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="Kart No">
                        {card.cardNumber || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Barkod">
                        {card.barcodeFormat ? (
                          <Tag color="blue">{card.barcodeFormat}</Tag>
                        ) : (
                          "-"
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="Takma Ad">
                        {card.nickname || "-"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Eklenme Tarihi">
                        {card.createdAt
                          ? new Date(card.createdAt).toLocaleString("tr-TR")
                          : "-"}
                      </Descriptions.Item>
                    </Descriptions>
                  </Space>
                </Card>
              ))}
            </Space>
          ) : (
            <Empty description="Diğer kart eklenmemiş" />
          )}
        </Card>

        <ClientStatistics
          statistics={client?.statistics}
          isLoading={isLoading}
        />
      </Space>
    </Spin>
  );
};

export default ClientDetails;
